import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverTeams() {
  const { baseurl, token, AdminId, setIsUnauthorised } =
    useGlobalContext();

  const [teams, setTeams] = useState([]);
  const [driverTeams, setDriverTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);

  /* ================= GET ALL TEAMS ================= */

  const fetchDriverTeams = useCallback(async () => {
  setTeamsLoading(true);

  try {
    const res = await axios.post(
      `${baseurl}/api/Driver/v2.0/getDriverTeamList`,
      { userid: "", adminId: String(AdminId) },
      { headers: { Authorization: `Basic ${token}` } }
    );

    if (res.data.responseCode === "00") {
      const data = res.data.data || [];
      setTeams(data);
      return data;            // ✅ IMPORTANT: now it returns teams
    }

    if (res.data.responseCode === "401") {
      setIsUnauthorised(true);
      return [];
    }

    toast.error(res.data.responseMessage || "Failed to fetch teams");
    return [];
  } catch (e) {
    console.error("Team API error:", e);
    toast.error("Failed to fetch teams");
    return [];
  } finally {
    setTeamsLoading(false);
  }
}, [baseurl, token, AdminId, setIsUnauthorised]);

  /* ================= GET DRIVER MULTIPLE TEAMS ================= */

  const fetchDriverMultipleTeams = async (driverId) => {
    try {
      setLoadingTeams(true);

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/getDriverMultipleTeams`,
        { driverId },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        const teams = res.data.data[0]?.driverTeams || [];
        setDriverTeams(teams);
        return teams;
      }

      if (res.data.responseCode === "02") {
        setDriverTeams([]);
        return [];
      }

      toast.error(res.data.responseMessage || "Failed to fetch driver teams");
      return [];
    } catch (err) {
      console.error(err);
      toast.error("Error fetching driver teams");
      return [];
    } finally {
      setLoadingTeams(false);
    }
  };

  /* ================= UPDATE DRIVER ACTIVE TEAM ================= */

  const updateDriverActiveTeam = async ({ adminId, driverId, teamId }) => {
    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/updateDriverActiveTeam`,
        { adminId, driverId, teamId },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success("Team activated successfully");
        return res.data;
      }

      if (res.data.responseCode === "01") {
        return res.data;
      }

      toast.error(res.data.responseMessage);
      return res.data;
    } catch (err) {
      console.error(err);
      toast.error("Error updating team");
      return null;
    }
  };

  /* ================= INSERT DRIVER MULTIPLE TEAMS ================= */

  const insertDriverMultipleTeams = async ({
    driverId,
    adminId,
    teamIds,
    primaryTeamId,
  }) => {
    try {
      const payload = {
        driverId,
        adminId: String(adminId),
        teamId: teamIds.map((id) => String(id)),
        primaryTeamId: String(primaryTeamId),
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/insertDriverMultipleTeams`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success("Teams assigned successfully");
        return true;
      }

      toast.error(res.data.responseMessage);
      return false;
    } catch (err) {
      console.error(err);
      toast.error("Error assigning teams");
      return false;
    }
  };

  /* ================= DELETE DRIVER MULTIPLE TEAMS ================= */

  const deleteDriverMultipleTeams = async ({ driverId, adminId, teamIds }) => {
    try {
      const payload = {
        driverId,
        adminId: String(AdminId),
        teamId: teamIds.map((id) => String(id)),
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/deleteDriverMultipleTeams`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success("Driver team deleted successfully");
        return true;
      }

      toast.error(res.data.responseMessage);
      return false;
    } catch (err) {
      console.error(err);
      toast.error("Error deleting driver team");
      return false;
    }
  };

  return {
    // team list
    teams,
    teamsLoading,
    fetchDriverTeams,

    // driver multiple teams
    driverTeams,
    loadingTeams,
    fetchDriverMultipleTeams,
    updateDriverActiveTeam,
    insertDriverMultipleTeams,
    deleteDriverMultipleTeams,
  };
}
